import React, { useState, useEffect, useMemo } from 'react';
import { Download, RefreshCw, Search, BarChart3, Users, Mail, LogOut } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AnalyticsTab from './AnalyticsTab.js';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function formatDate(ts) {
  return new Date(ts).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function AdminWaitlist() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('waitlist');
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [digestSending, setDigestSending] = useState(false);
  const [digestMsg, setDigestMsg] = useState('');

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } });

  const fetchWaitlist = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(API + '/waitlist', authHeaders());
      setEntries(res.data);
    } catch { setError('Failed to load waitlist entries'); }
    finally { setLoading(false); }
  };

  const fetchAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const res = await axios.get(API + '/analytics/summary', authHeaders());
      setAnalytics(res.data);
    } catch (e) { console.error(e); }
    finally { setAnalyticsLoading(false); }
  };

  useEffect(() => { fetchWaitlist(); }, []);
  useEffect(() => { if (activeTab === 'analytics' && !analytics) fetchAnalytics(); }, [activeTab, analytics]);

  const sendDigest = async () => {
    setDigestSending(true);
    setDigestMsg('');
    try {
      await axios.post(API + '/admin/digest', {}, authHeaders());
      setDigestMsg('Digest email sent!');
      setTimeout(() => setDigestMsg(''), 4000);
    } catch {
      setDigestMsg('Failed to send digest');
    } finally {
      setDigestSending(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return entries;
    const q = searchQuery.toLowerCase();
    return entries.filter(e =>
      (e.first_name || '').toLowerCase().includes(q) ||
      (e.last_name || '').toLowerCase().includes(q) ||
      (e.email || '').toLowerCase().includes(q) ||
      (e.phone_os || '').toLowerCase().includes(q)
    );
  }, [entries, searchQuery]);

  const androidCount = entries.filter(e => e.phone_os === 'android').length;
  const iosCount = entries.filter(e => e.phone_os === 'ios').length;

  const exportCSV = () => {
    if (!entries.length) return;
    const esc = (s) => '"' + (s || '').replace(/"/g, '""') + '"';
    const hdr = '#,First Name,Last Name,Email,Phone OS,Kids Ages,Signed Up';
    const rows = entries.map((e, i) =>
      [i + 1, esc(e.first_name), esc(e.last_name), esc(e.email), e.phone_os || '', esc(e.kids_ages), formatDate(e.timestamp)].join(',')
    );
    const blob = new Blob([hdr + '\n' + rows.join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'chaching-waitlist-' + new Date().toISOString().slice(0, 10) + '.csv';
    a.click();
  };

  return (
    <div className="admin-container" data-testid="admin-dashboard">
      <div className="admin-header">
        <h1 className="admin-title">BuckTime! Admin</h1>
        <div className="admin-tabs">
          <button className={'admin-tab-btn' + (activeTab === 'waitlist' ? ' active' : '')} onClick={() => setActiveTab('waitlist')} data-testid="tab-waitlist">
            <Users size={16} /> Waitlist
          </button>
          <button className={'admin-tab-btn' + (activeTab === 'analytics' ? ' active' : '')} onClick={() => setActiveTab('analytics')} data-testid="tab-analytics">
            <BarChart3 size={16} /> Analytics
          </button>
          <button className="admin-tab-btn logout-btn" onClick={handleLogout} data-testid="logout-btn">
            <LogOut size={16} /> Logout
          </button>
        </div>
      </div>

      {activeTab === 'waitlist' && (
        <div className="waitlist-tab">
          <div className="waitlist-stats-row">
            <div className="waitlist-stat"><div className="stat-value" data-testid="stat-total">{entries.length}</div><div className="stat-label">Total Signups</div></div>
            <div className="waitlist-stat"><div className="stat-value android-color" data-testid="stat-android">{androidCount}</div><div className="stat-label">Android</div></div>
            <div className="waitlist-stat"><div className="stat-value ios-color" data-testid="stat-ios">{iosCount}</div><div className="stat-label">iOS</div></div>
            <div className="waitlist-stat"><div className="stat-value stat-value-sm" data-testid="stat-latest">{entries.length > 0 ? formatDate(entries[0]?.timestamp).split(',')[0] : 'N/A'}</div><div className="stat-label">Latest Signup</div></div>
          </div>

          <div className="waitlist-toolbar">
            <div className="search-wrapper">
              <Search size={16} className="search-icon" />
              <input type="text" placeholder="Search by name, email, or OS..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="search-input" data-testid="search-input" />
            </div>
            <button onClick={fetchWaitlist} disabled={loading} className="btn-outline" data-testid="refresh-btn">
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Refresh
            </button>
            <button onClick={exportCSV} disabled={!entries.length} className="btn-primary" data-testid="export-csv-btn">
              <Download size={16} /> Export CSV
            </button>
            <button onClick={sendDigest} disabled={digestSending} className="btn-digest" data-testid="send-digest-btn">
              <Mail size={16} /> {digestSending ? 'Sending...' : 'Send Digest'}
            </button>
            {digestMsg && <span className="digest-msg" data-testid="digest-msg">{digestMsg}</span>}
          </div>

          <div className="waitlist-table-card">
            <div className="table-header-row">
              <h3>Waitlist Entries</h3>
              <span className="entry-count">{searchQuery ? filtered.length + ' of ' + entries.length : entries.length + ' total'}</span>
            </div>
            {loading ? (
              <div className="loading-state">Loading waitlist entries...</div>
            ) : error ? (
              <div className="error-state">{error}</div>
            ) : filtered.length === 0 ? (
              <div className="empty-state">{searchQuery ? 'No matching entries' : 'No waitlist entries yet'}</div>
            ) : (
              <div className="table-scroll">
                <table className="admin-table">
                  <thead><tr><th>#</th><th>First Name</th><th>Last Name</th><th>Email</th><th>Phone OS</th><th>Kids' Ages</th><th>Signed Up</th></tr></thead>
                  <tbody>
                    {filtered.map((e, i) => (
                      <tr key={e.id || i}>
                        <td>{i + 1}</td>
                        <td>{e.first_name}</td>
                        <td>{e.last_name}</td>
                        <td className="email-cell">{e.email}</td>
                        <td><span className={'os-badge ' + (e.phone_os === 'android' ? 'os-android' : 'os-ios')}>{e.phone_os === 'android' ? 'Android' : 'iOS'}</span></td>
                        <td>{e.kids_ages || '—'}</td>
                        <td className="date-cell">{formatDate(e.timestamp)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'analytics' && (
        <AnalyticsTab analytics={analytics} analyticsLoading={analyticsLoading} onRefresh={fetchAnalytics} />
      )}
    </div>
  );
}

export default AdminWaitlist;
